/** Your word of the year: a swarm of your words gathers into one, then your top five. */

import { useRef, type CSSProperties } from "react";

import { Fit, Scene, Zone, useStageExtra, type BlobSpec } from "../../story/stage";
import { useTimeline } from "../../story/timeline";
import { wr } from "../../theme";
import type { You } from "../../types";
import { count } from "../shared";

const BLOBS: BlobSpec[] = [
  { colour: "teal", x: 1040, y: 1880, r: 260, seed: 5.2, wob: 0.18 },
  { colour: "yellow", x: 1040, y: 520, r: 150, seed: 6.1, wob: 0.22 },
];

const SWARM_SIZE = 44;
const ROWS = SWARM_SIZE / 4;
const SWARM_COLOURS = ["ink", "white", "blurple", "fuchsia", "ink"].map(wr);
const CLIP: CSSProperties = { minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

/** The video's seeded random, so the swarm lands in the same places every time. */
function seeded(start: number) {
  let seed = start;
  return () => ((seed = (seed * 16807) % 2147483647) - 1) / 2147483646;
}

type Bit = { word: string; x: number; y: number; size: number; colour: string; dx: number; dy: number; rot: number };

/**
 * The swarm, laid out as the video does: four loose columns of rows from the
 * top down. We only know a handful of your words, so they repeat, shuffled.
 * On a taller stage the rows spread out to fill it. They sit in the big
 * word's zone, which moves down by half the extra height, so they start that
 * much higher to still begin under the heading.
 */
function swarmOf(words: string[], extra: number): Bit[] {
  if (!words.length) return [];
  const rand = seeded(26);
  const band = 122 + extra / ROWS;
  const lift = Math.round(extra / 2);
  const pool = Array.from({ length: SWARM_SIZE }, (_, i) => words[i % words.length]);
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.map((word, i) => {
    const length = [...word].length;
    // A long word gets smaller rather than running off the stage.
    const size = Math.min(Math.round(40 + rand() * 56), Math.floor(936 / (0.5 * length)));
    const width = size * 0.5 * length;
    const col = i % 4;
    const row = Math.floor(i / 4);
    const x = Math.min(1008 - width, 72 + col * 234 + rand() * Math.max(0, 234 - width));
    const y = 400 - lift + row * band + rand() * (band - size * 0.9);
    return { word, x, y, size, colour: SWARM_COLOURS[i % SWARM_COLOURS.length], dx: (rand() - 0.5) * 180, dy: (rand() - 0.5) * 180, rot: (rand() - 0.5) * 30 };
  });
}

export function Words({ words }: { words: You["words"] }) {
  const { signature } = words;
  const top = words.top.slice(0, 5);
  const extra = useStageExtra();
  const swarm = swarmOf([...new Set([signature, ...top.map((w) => w.word)].filter((w): w is string => !!w))], extra);
  // The timeline is built before the stage has measured the screen, so the rush reads where the swarm is now.
  const placed = useRef(swarm);
  placed.current = swarm;
  // We only know how often you said it when it is also one of your top words.
  const said = top.find((w) => w.word === signature)?.count;

  const root = useTimeline((s) => {
    s.rise(".w-kick", 0.35, { y: 0 });
    const bits = s.q(".w-swarm");
    bits.forEach((el, i) => {
      const b = swarm[i];
      const at = 0.3 + s.random() * 0.7;
      s.tl.set(el, { rotation: b.rot }, 0);
      s.tl.fromTo(el, { scale: 0, autoAlpha: 0 }, { scale: 1, autoAlpha: 1, duration: 0.5, ease: "back.out(2)" }, at);
      s.tl.fromTo(el, { x: 0, y: 0 }, { x: b.dx, y: b.dy, duration: 2.3 - at, ease: "sine.inOut" }, at);
    });
    // Then they all rush into the middle, where the word (or your top words) appear.
    bits.forEach((el, i) => {
      const b = swarm[i];
      s.tl.to(el, { x: 540 - b.x - 30, y: () => 950 - placed.current[i].y - 30, scale: 0.15, rotation: 0, autoAlpha: 0, duration: 0.5, ease: "power3.in" }, 2.3 + s.random() * 0.25);
    });
    if (bits.length) {
      s.sound("whoosh", 0.3, 1, { length: 0.8 });
      // The swarm is sucked into the middle, faster and faster, before the word pops out.
      s.sound("riser", 2.3, 1.1, { length: 0.55 });
    }
    if (signature) {
      s.pop(".w-big", 2.85, { from: 0.15, dur: 0.7, ease: "back.out(2.6)" });
      s.rise(".w-line", 3.25, { y: 26, stagger: 0.18 });
    }
    if (top.length) {
      if (signature) s.rise(".w-top", 3.7, { y: 0, sound: false });
      s.pop(".wchip", signature ? 3.8 : 2.85, { from: 0.5, stagger: 0.08, dur: 0.45 });
    }
  });

  const chips = top.length ? (
    // With no word of the year, the top words take the middle of the card instead.
    <div className="wchips" style={signature ? undefined : { top: 960, transform: "translateY(-50%)" }}>
      {top.map((w) => (
        <div key={w.word} className="wchip" style={{ maxWidth: "100%" }}>
          <b style={CLIP}>{w.word}</b>
          <span>{count(w.count)}</span>
        </div>
      ))}
    </div>
  ) : null;

  return (
    <div ref={root} className="contents">
      <Scene blobs={BLOBS}>
        <Zone at="top">
          <div className="kick w-kick">{signature ? "Your word of the year" : "Your top words"}</div>
          {!signature && !top.length ? (
            <div className="line" style={{ top: 400 }}>
              Not enough words this year to pick from.
            </div>
          ) : null}
        </Zone>
        {/* The swarm rushes into the big word, so the two share a zone. */}
        <Zone at="middle">
          {swarm.map((b, i) => (
            <div
              key={i}
              aria-hidden
              className="abs disp wr-hide w-swarm"
              style={{ left: Math.round(b.x), top: Math.round(b.y), fontSize: b.size, color: b.colour, whiteSpace: "nowrap" }}
            >
              {b.word}
            </div>
          ))}
          {signature ? (
            <>
              <Fit size={300} min={90} className="abs disp w-big" style={{ left: 60, right: 60, top: 780, textAlign: "center" }}>
                {signature}
              </Fit>
              {said !== undefined ? (
                <div className="line w-line" style={{ top: 1120, fontSize: 58 }}>
                  You said it <b>{count(said)}</b> {said === 1 ? "time" : "times"}.
                </div>
              ) : null}
              <div className="line w-line" style={{ top: said !== undefined ? 1200 : 1120 }}>
                Lots from you, rare from everyone else.
              </div>
            </>
          ) : (
            chips
          )}
        </Zone>
        {signature ? (
          <Zone at="bottom">
            {top.length ? (
              <div className="kick w-top" style={{ top: 1370 }}>
                {top.length === 1 ? "Your top word" : `Your top ${top.length} words`}
              </div>
            ) : null}
            {chips}
          </Zone>
        ) : null}
      </Scene>
    </div>
  );
}
